import Styles from './BrandProfileForStrategies.module.scss'
import { Formik } from 'formik'
import { BrandProfileProps } from 'components/FirstTimeExperienceModal/steps/BrandProfile/Types'

import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { StickyFooter, FooterButtons } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Button } from 'blaze-ui/Button'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Data } from 'hooks/queries/brandProfiles/useBrandProfileAnalysis'
import { AIJobStatus, BusinessType, LogoVariant } from 'types'
import { usePollBrandProfileAnalysis } from 'components/FirstTimeExperienceModal/steps/BrandProfile/usePollBrandProfileAnalysis'
import { reportError } from 'utils/error'
import { useDeleteBrandKitSource } from 'hooks/queries/useDeleteBrandKitSource'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useDeleteBrandProfile } from 'hooks/queries/brandProfiles/useDeleteBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Heading } from 'blaze-ui/Heading/Heading'

import { Field } from 'components/Wizards/Field/Field'
import { useUpdateBrandProfile } from 'hooks/queries/brandProfiles/useUpdateBrandProfile'
import {
  parseProfileSections,
  reconstructMarkdown,
  ProfileSection,
} from 'screens/BrandKits/components/BrandProfile/markdownUtils'
import { EditableSection } from './EditableSection'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { LoadingState } from './LoadingState'
import { useOnboardingStrategiesEnabled, useOnboardingV4Enabled } from 'flows/virtualMarketerOnboarding/steps'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'blaze-ui/Text/Text'
import { DEFAULT_LANGUAGE, LanguageInput } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { BUSINESS_TYPE_OPTIONS } from 'screens/VirtualMarketerOnboarding/GoalSelection/utils'
import cx from 'classnames'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useUpdateRegistrationData } from 'hooks/queries/useUpdateRegistrationData'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { AudiencePreferences } from 'screens/BrandKits/components/ContentPreferences/AudiencePreferences/AudiencePreferences'
import { RepresentationPreferences } from 'screens/BrandKits/components/ContentPreferences/RepresentationPreferences/RepresentationPreferences'
import { noop } from 'lodash'
import { PrimaryMarketLocations } from 'screens/BrandKits/components/ContentPreferences/PrimaryMarketLocations/PrimaryMarketLocations'
import { Logo } from 'screens/BrandKit/BrandStyles/LogosSection/LogoVariants/Logo'
import LogoVariantStyles from 'screens/BrandKit/BrandStyles/LogosSection/LogoVariants/LogoVariants.module.scss'
import { Edit1 } from 'blaze-ui/icons'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const BrandProfileForStrategies = ({
  brandProfile,
  onNext,
  onBack,
  analysisId,
  initialProfile,
  language,
  onLanguageChange,
}: BrandProfileProps) => {
  const isFastWebsiteScrapingEnabled = useFlipperFlag('fast_website_scraping')
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const isV4 = useOnboardingV4Enabled()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [profile, setProfile] = useState<string>(() => {
    if (initialProfile) return initialProfile
    if (brandProfile.profile instanceof Object) return ''
    return `${brandProfile.profile}`
  })
  const [refreshProfile, setRefreshProfile] = useState(false)

  const { trackBrandProfileEdited, trackIndustryTypeSelected } = useOnboardingTracking()

  const [sections, setSections] = useState<ProfileSection[]>([])
  const [sectionsToEdit, setSectionsToEdit] = useState<ProfileSection[]>([])
  const [brandProfileName, setBrandProfileName] = useState<string>()
  const [selectedIndustryType, setSelectedIndustryType] = useState<BusinessType>()
  const [audienceMinAge, setAudienceMinAge] = useState<number>()
  const [audienceMaxAge, setAudienceMaxAge] = useState<number>()
  const [audienceGender, setAudienceGender] = useState<string>()
  const [representationAgeRange, setRepresentationAgeRange] = useState<string>()
  const [representationCulture, setRepresentationCulture] = useState<string>()
  const [representationGender, setRepresentationGender] = useState<string>()
  const [primaryMarketLocations, setPrimaryMarketLocations] = useState<string[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language || DEFAULT_LANGUAGE)
  const [isLogoUploading, setIsLogoUploading] = useState(false)

  const { data: brandKitData } = useBrandKit(brandProfile.brand_kit.id, {
    cacheTime: 1000 * 60 * 5,
    refetchInterval: (data) => {
      if (!profile) return false
      if (data?.brand_kit?.status === AIJobStatus.Successful) return false

      return 1000
    },
  })

  const { mutate: deleteBrandProfile } = useDeleteBrandProfile({ id: brandProfile.id })
  const { mutate: deleteBrandKitSource } = useDeleteBrandKitSource()
  const { mutateAsync: updateBrandProfile, isLoading: isUpdatingBrandProfile } = useUpdateBrandProfile({
    id: brandProfile.id,
  })
  const { mutateAsync: updateRegistrationData, isLoading: isUpdatingRegistrationData } = useUpdateRegistrationData()
  const { mutateAsync: updateBrandKit, isLoading: isUpdatingBrandKit } = useUpdateBrandKit({
    id: brandProfile.brand_kit.id,
  })

  const hasInitialized = useRef(false)
  const brandKit = brandKitData?.brand_kit

  const handleBack = useCallback(
    async (withError = false) => {
      setProfile('')
      deleteBrandProfile()
      brandKit?.brand_kit_sources?.forEach((source) => deleteBrandKitSource({ brandKitId: brandKit.id, id: source.id }))
      if (withError) {
        showErrorMessageToast("We could not analyze your website, please verify that it's correct and try again.")
      }
      onBack()
    },
    [deleteBrandProfile, brandKit?.id, brandKit?.brand_kit_sources, deleteBrandKitSource, onBack],
  )

  const handleAnalysisComplete = useCallback(
    async (data: Data) => {
      const { status } = data.brand_profile_analysis
      if (status === AIJobStatus.Successful) {
        await queryClient.refetchQueries(['workspaces', workspaceId, 'brandProfiles', 'detail', brandProfile.id])
        setRefreshProfile(true)
        return
      }

      if (status === AIJobStatus.Failed || status === AIJobStatus.Expired) {
        handleBack(true)
      }
    },
    [queryClient, workspaceId, brandProfile.id, handleBack],
  )

  const handleSectionUpdateForStrategies = useCallback(
    (sectionKey: string, newContent: string) => {
      const lastDashIndex = sectionKey.lastIndexOf('-')
      const sectionIndex = parseInt(sectionKey.substring(lastDashIndex + 1), 10)

      const updatedSections = sections.map((section, idx) =>
        idx === sectionIndex ? { ...section, content: newContent } : section,
      )

      setSections(updatedSections)
      setProfile(reconstructMarkdown(updatedSections))
    },
    [sections],
  )

  const { data } = usePollBrandProfileAnalysis(
    { id: analysisId, brandProfileId: brandProfile.id },
    { onSuccess: handleAnalysisComplete },
  )

  const brandVisualKitId = brandKit?.brand_visual_kit?.id
  const contentPreferences = brandProfile?.brand_kit?.content_preferences
  const analysisStatus = data?.brand_profile_analysis.status
  const completedStates = [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired]
  const hasFinished = analysisStatus && completedStates.includes(analysisStatus)
  const isDisabled =
    data?.brand_profile_analysis.status !== AIJobStatus.Successful ||
    isUpdatingBrandProfile ||
    isUpdatingRegistrationData ||
    isUpdatingBrandKit ||
    isLogoUploading

  const handleUpdateBrandProfile = useCallback(async () => {
    if (!brandProfile?.id) return

    const fullMarkdown = reconstructMarkdown(sections)
    try {
      await updateBrandProfile({
        profile: fullMarkdown,
        name: brandProfileName,
        business_type: selectedIndustryType,
        audience_min_age: audienceMinAge,
        audience_max_age: audienceMaxAge,
        audience_gender: audienceGender,
        representation_age_range: representationAgeRange,
        representation_culture: representationCulture,
        representation_gender: representationGender,
        primary_market_locations: primaryMarketLocations.join(', '),
      })
      trackBrandProfileEdited()
    } catch (error) {
      reportError(error)
      showErrorMessageToast('Failed to save changes. Please try again.')
      throw error
    }
  }, [
    brandProfile?.id,
    sections,
    updateBrandProfile,
    brandProfileName,
    selectedIndustryType,
    audienceMinAge,
    audienceMaxAge,
    audienceGender,
    representationAgeRange,
    representationCulture,
    representationGender,
    primaryMarketLocations,
    trackBrandProfileEdited,
  ])

  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({
    id: brandProfile.brand_plan.id,
  })

  useEffect(() => {
    if (!!hasInitialized.current || !hasFinished || !brandProfile?.name) return
    hasInitialized.current = true

    if (!brandProfileName) setBrandProfileName(brandProfile?.name)
    if (!audienceMinAge) setAudienceMinAge(brandProfile?.audience_min_age)
    if (!audienceMaxAge) setAudienceMaxAge(brandProfile?.audience_max_age)
    if (!audienceGender) setAudienceGender(brandProfile?.audience_gender)
    if (!representationAgeRange) setRepresentationAgeRange(brandProfile?.representation_age_range)
    if (!representationCulture) setRepresentationCulture(brandProfile?.representation_culture)
    if (!representationGender) setRepresentationGender(brandProfile?.representation_gender)
    if (!primaryMarketLocations?.length)
      setPrimaryMarketLocations(Array.from(brandProfile?.primary_market_locations?.split(', ') ?? []))
  }, [hasInitialized.current, hasFinished, brandProfile?.name]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasFinished && !selectedIndustryType) {
      setSelectedIndustryType((brandProfile?.business_type || 'service') as BusinessType)
    }
  }, [hasFinished, selectedIndustryType, brandProfile?.business_type])

  useEffect(() => {
    if (typeof brandProfile.profile === 'string' && brandProfile.profile.length > 0 && profile.length === 0) {
      setProfile(brandProfile.profile)
    }
  }, [brandProfile.profile, profile.length])

  useEffect(() => {
    if (
      refreshProfile &&
      typeof brandProfile.profile === 'string' &&
      brandProfile.profile.length > 0 &&
      brandProfile.profile !== profile
    ) {
      setProfile(brandProfile.profile)
      setRefreshProfile(false)
    }
  }, [brandProfile.profile, refreshProfile, profile])

  useEffect(() => {
    if (profile.length > 0) {
      const parsedSections = parseProfileSections(profile, !!isOnboardingStrategiesEnabled)

      setSections(parsedSections)
      setSectionsToEdit(
        parsedSections
          .filter((_, index) => index === 0 || index === 1)
          .map((section, index) => ({
            ...section,
            title: index === 0 ? 'Elevator pitch' : 'Market positioning',
          })),
      )
    } else {
      setSections([])
    }
  }, [profile, isOnboardingStrategiesEnabled])

  const handleNext = async () => {
    if (!selectedIndustryType) return

    try {
      await Promise.all([
        handleUpdateBrandProfile(),
        trackIndustryTypeSelected(selectedIndustryType),
        updateRegistrationData({ industryType: selectedIndustryType }),
        updateBrandPlan({
          business_type: selectedIndustryType,
        }),
        updateBrandKit({
          content_preferences: {
            ...contentPreferences,
            audience_min_age: audienceMinAge,
            audience_max_age: audienceMaxAge,
            audience_gender: audienceGender,
            representation_age_range: representationAgeRange,
            representation_culture: representationCulture,
            representation_gender: representationGender,
            primary_market_locations: Array.from(primaryMarketLocations),
            language: selectedLanguage,
          },
        }),
      ])
      onNext(profile, selectedIndustryType)
    } catch (error) {
      showErrorMessageToast('Failed to save changes. Please try again.')
      reportError(error)
    }
  }

  if (hasFinished) {
    return (
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              {!isMobile && (
                <Button variant="tertiary" size="lg" onPress={() => handleBack()} aria-label="Back">
                  Back
                </Button>
              )}
              <Button
                size="lg"
                onPress={handleNext}
                isDisabled={isDisabled || !selectedIndustryType || !brandProfileName}
                aria-label="Looks good"
                fullWidth={isMobile}
              >
                Looks good
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <>
          <div className={Styles.container}>
            <div className={Styles.topSection}>
              <Heading level={isMobile ? 2 : 1}>First, let’s confirm some basics about your business </Heading>
            </div>
            <div className={Styles.form}>
              <div className={Styles.businessNameField}>
                {isMobile ? (
                  <Text variant="label" color="var(--dark-60)">
                    Business name
                  </Text>
                ) : (
                  <Heading level={3}>Business name</Heading>
                )}
                <Input
                  value={brandProfileName}
                  onChange={(e) => setBrandProfileName(e.target.value)}
                  name="brand-profile-name"
                  size="xl"
                />
              </div>
              <div className={Styles.businessAndIndustry}>
                <div className={Styles.fieldContainer}>
                  <div className={Styles.titleAndDescription}>
                    <Heading level={isMobile ? 3 : 4}>Select your type of business</Heading>
                    {!isMobile && <Text color="var(--dark-60)">This will affect your content plan</Text>}
                  </div>
                  <div className={Styles.businessTypes}>
                    {BUSINESS_TYPE_OPTIONS.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => setSelectedIndustryType(option.id)}
                        className={cx(Styles.businessCard, {
                          [Styles.selected]: selectedIndustryType === option.id,
                        })}
                      >
                        {isMobile ? (
                          <div className={Styles.mobileIcon} style={{ backgroundColor: option.backgroundColor }}>
                            {<option.icon size={28} color={option.iconColor} />}
                          </div>
                        ) : (
                          <option.icon size={28} />
                        )}
                        <div className={Styles.businessCardContent}>
                          {isMobile ? <Heading level={5}>{option.title}</Heading> : <Text>{option.title}</Text>}
                          <div className={Styles.description}>
                            <Text variant={isMobile ? 'secondary' : 'metadata'} color="var(--dark-60)">
                              {option.description}
                            </Text>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {sectionsToEdit[0] && (
                <EditableSection
                  section={sectionsToEdit[0]}
                  sectionKey={`${sectionsToEdit[0].title}-0`}
                  onSectionUpdate={handleSectionUpdateForStrategies}
                  isUpdating={isUpdatingBrandProfile}
                  isEditing
                  editInline
                  sectionClassName={Styles.section}
                  headerClassName={Styles.sectionHeader}
                  contentClassName={Styles.sectionContent}
                  editingClassName={Styles.editingContainer}
                  editorClassName={Styles.proseEditor}
                />
              )}
              {(brandVisualKitId || (isFastWebsiteScrapingEnabled && brandKit?.logo_url)) && (
                <div className={Styles.logoContainer}>
                  <Heading level={3}>Logo</Heading>
                  {brandVisualKitId ? (
                    <Logo
                      brandVisualKitId={brandVisualKitId}
                      variant={LogoVariant.Original}
                      logos={brandKit?.brand_visual_kit?.logos || []}
                      onLogoChange={() =>
                        queryClient.refetchQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKit?.id])
                      }
                      onUploadingChange={setIsLogoUploading}
                      className={Styles.logo}
                      showDelete={false}
                      showActions
                      editTrigger={
                        <div className={Styles.editButton}>
                          <Button variant="secondary" frontIcon={Edit1} aria-label="Edit" size={isMobile ? 'sm' : 'md'}>
                            Edit
                          </Button>
                        </div>
                      }
                    />
                  ) : (
                    <div className={cx(LogoVariantStyles.logo, Styles.logo)} data-testid="logo-Original">
                      <div className={cx(LogoVariantStyles.logoActions, LogoVariantStyles.showActions)} />
                      <img src={brandKit?.logo_url ?? undefined} alt="" />
                    </div>
                  )}
                </div>
              )}
              <div className={Styles.audienceAndLocations}>
                <AudiencePreferences
                  minAge={audienceMinAge}
                  maxAge={audienceMaxAge}
                  gender={audienceGender}
                  onMinAgeUpdate={setAudienceMinAge}
                  onMaxAgeUpdate={setAudienceMaxAge}
                  onGenderUpdate={setAudienceGender}
                  headingLevel={2}
                  labelColor="var(--dark-40)"
                  className={Styles.audiencePreferencesFields}
                />
                <PrimaryMarketLocations
                  locations={new Set(primaryMarketLocations)}
                  onUpdate={(newSet) => setPrimaryMarketLocations(Array.from(newSet))}
                  headingLevel={2}
                  size="lg"
                  className={Styles.primaryMarketLocations}
                  label="Audience"
                  subLabel="Primary Market Locations"
                  labelColor="var(--dark-40)"
                />
              </div>
              <RepresentationPreferences
                ageRange={representationAgeRange}
                gender={representationGender}
                culture={representationCulture}
                onAgeRangeUpdate={setRepresentationAgeRange}
                onGenderUpdate={setRepresentationGender}
                onCultureUpdate={setRepresentationCulture}
                onDetailsUpdate={noop}
                hideDetails
                headingLevel={2}
                labelColor="var(--dark-40)"
                inlineAgeAndGender={isMobile}
                className={Styles.representationPreferences}
              />
              <div className={Styles.primaryLanguageField}>
                <Field
                  label="Primary Language"
                  headingLevel={2}
                  labelColor="var(--dark-40)"
                  data-testid="primary-language"
                >
                  <LanguageInput
                    languageValue={selectedLanguage}
                    onLanguageChange={(lang) => {
                      setSelectedLanguage(lang)
                      onLanguageChange?.(lang)
                    }}
                    size="lg"
                    matchReferenceWidth={true}
                    wrapperClassName={Styles.primaryLanguageMobileSelect}
                  />
                </Field>
              </div>
              {sectionsToEdit[1] && (
                <EditableSection
                  section={sectionsToEdit[1]}
                  sectionKey={`${sectionsToEdit[1].title}-1`}
                  onSectionUpdate={handleSectionUpdateForStrategies}
                  isUpdating={isUpdatingBrandProfile}
                  isEditing
                  editInline
                  sectionClassName={Styles.section}
                  headerClassName={Styles.sectionHeader}
                  contentClassName={Styles.sectionContent}
                  editingClassName={Styles.editingContainer}
                  editorClassName={Styles.proseEditor}
                />
              )}
            </div>
          </div>
        </>
      </StickyFooterProvider>
    )
  }

  if (isV4) return null

  return (
    <StickyFooterProvider
      footer={
        <StickyFooter>
          <FooterButtons>
            {!isMobile && (
              <Button variant="tertiary" size="lg" onPress={() => handleBack()} aria-label="Back">
                Back
              </Button>
            )}
            {!isMobile && (
              <Button size="lg" isDisabled aria-label="Looks good" fullWidth={isMobile}>
                Looks good
              </Button>
            )}
          </FooterButtons>
        </StickyFooter>
      }
    >
      <div className={Styles.titleContainer}>
        <Heading level={isMobile ? 2 : 1}>
          Building your business profile
          <LoadingEllipses />
        </Heading>
      </div>
      <LoadingState
        url={brandProfile.url}
        handleBack={handleBack}
        cancelOnError={false}
        finishedLoading={profile.length > 1 && hasFinished}
      />
    </StickyFooterProvider>
  )
}

const BrandProfileForStrategiesFormikContainer = ({ brandProfile, ...rest }: BrandProfileProps) => (
  <Formik onSubmit={() => {}} enableReinitialize initialValues={{}}>
    <BrandProfileForStrategies brandProfile={brandProfile} {...rest} />
  </Formik>
)

export { BrandProfileForStrategiesFormikContainer as BrandProfileForStrategies }
